// Public-site navigation config.

import type { ComponentType, SVGProps } from "react";
import {
  DollarSign,
  Wallet,
  Gavel,
  FileX,
  Landmark,
  Scale,
  ShieldAlert,
  Building2,
} from "lucide-react";

export type NavIcon = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

export interface NavItem {
  name: string;
  href: string;
}

export interface NavColumn {
  title: string;
  href?: string;
  items: NavItem[];
}

export interface ServicePrimaryCard {
  slug: string;
  href: string;
  headline: string;
  desc: string;
  Icon: NavIcon;
}

/**
 * Problem-first primary cards shown at the top of the Services mega menu.
 * Mirrors the MLOTax strategy: lead with the taxpayer's pain, not our taxonomy.
 */
export const servicesPrimaryCards: ServicePrimaryCard[] = [
  {
    slug: "offer-in-compromise",
    href: "/services/tax-debt-relief/offer-in-compromise",
    headline: "I Owe More Than I Can Pay",
    desc: "Settle IRS debt for a fraction with an Offer in Compromise.",
    Icon: DollarSign,
  },
  {
    slug: "wage-garnishment",
    href: "/services/irs-collections-defense/wage-garnishment",
    headline: "The IRS Is Taking My Paycheck",
    desc: "Stop wage garnishments and bank levies — often within 48 hours.",
    Icon: Wallet,
  },
  {
    slug: "irs-audit-representation",
    href: "/services/audit-defense/irs-audit-representation",
    headline: "I'm Being Audited by the IRS",
    desc: "We handle it end-to-end. You never face the auditor alone.",
    Icon: Gavel,
  },
  {
    slug: "back-taxes",
    href: "/services/tax-debt-relief/back-taxes",
    headline: "I Haven't Filed in Years",
    desc: "Catch up safely and quietly. No judgment — a clear path forward.",
    Icon: FileX,
  },
  {
    slug: "tax-liens",
    href: "/services/irs-collections-defense/tax-liens",
    headline: "There's a Lien on My Property",
    desc: "Release or discharge federal tax liens so you can sell or refinance.",
    Icon: Landmark,
  },
];

/** Icons for the "Browse by category" footer pill row. */
export const categoryIcons: Record<string, NavIcon> = {
  "/services/tax-debt-relief": Scale,
  "/services/irs-collections-defense": ShieldAlert,
  "/services/audit-defense": Gavel,
  "/services/business-tax": Building2,
};

export const servicesMenu: NavColumn[] = [
  {
    title: "Tax Debt Relief",
    href: "/services/tax-debt-relief",
    items: [
      { name: "Offer in Compromise", href: "/services/tax-debt-relief/offer-in-compromise" },
      { name: "Installment Agreement", href: "/services/tax-debt-relief/installment-agreement" },
      { name: "Currently Not Collectible", href: "/services/tax-debt-relief/currently-not-collectible" },
      { name: "Penalty Abatement", href: "/services/tax-debt-relief/penalty-abatement" },
      { name: "Innocent Spouse Relief", href: "/services/tax-debt-relief/innocent-spouse-relief" },
      { name: "Back Taxes Help", href: "/services/tax-debt-relief/back-taxes" },
    ],
  },
  {
    title: "IRS Collections Defense",
    href: "/services/irs-collections-defense",
    items: [
      { name: "Wage Garnishment Release", href: "/services/irs-collections-defense/wage-garnishment" },
      { name: "Bank Levy Release", href: "/services/irs-collections-defense/bank-levy" },
      { name: "Tax Lien Help", href: "/services/irs-collections-defense/tax-liens" },
      { name: "IRS Seizures", href: "/services/irs-collections-defense/irs-seizures" },
      { name: "Stop IRS Collections", href: "/services/irs-collections-defense/stop-collections" },
    ],
  },
  {
    title: "Audit & Examination",
    href: "/services/audit-defense",
    items: [
      { name: "IRS Audit Representation", href: "/services/audit-defense/irs-audit-representation" },
      { name: "IRS Appeals", href: "/services/audit-defense/irs-appeals" },
      { name: "CP2000 Response", href: "/services/audit-defense/cp2000-response" },
      { name: "Examination Reconsideration", href: "/services/audit-defense/exam-reconsideration" },
    ],
  },
  {
    title: "Business Tax",
    href: "/services/business-tax",
    items: [
      { name: "Payroll Tax Problems", href: "/services/business-tax/payroll-tax" },
      { name: "Trust Fund Recovery Penalty", href: "/services/business-tax/trust-fund-recovery" },
      { name: "941 / 940 Resolution", href: "/services/business-tax/employment-tax" },
      { name: "Sales & Use Tax", href: "/services/business-tax/sales-and-use-tax" },
      { name: "Business Tax Preparation", href: "/services/business-tax/tax-preparation" },
    ],
  },
];

export const aboutMenu: NavColumn[] = [
  {
    title: "Our Firm",
    items: [
      { name: "About CTR", href: "/about" },
      { name: "Franklin Sofi, MBA, CPA", href: "/about/franklin-sofi" },
      { name: "How It Works", href: "/how-it-works" },
      { name: "Reviews", href: "/reviews" },
      { name: "Case Results", href: "/results" },
      { name: "FAQ", href: "/faq" },
      { name: "Free Consultation", href: "/free-consultation" },
      { name: "Contact Us", href: "/contact" },
      { name: "Ayuda en Español", href: "/ayuda" },
    ],
  },
];

export const resourcesMenu: NavColumn[] = [
  {
    title: "Resources",
    items: [
      { name: "IRS Notice Library", href: "/resources/irs-notices" },
      { name: "IRS Form Library", href: "/resources/irs-forms" },
      { name: "Tax Glossary", href: "/resources/glossary" },
      { name: "Notice Lookup Tool", href: "/resources/notice-lookup" },
      { name: "CPA vs. Tax Attorney", href: "/resources/cpa-vs-tax-attorney-for-irs-help" },
      { name: "Tax Resolution Pricing", href: "/resources/how-much-does-tax-resolution-cost" },
      { name: "Resolution Timelines", href: "/resources/how-long-does-tax-resolution-take" },
      { name: "Blog", href: "/blog" },
    ],
  },
];

export type DropdownKey = "services" | "about" | "resources" | "serviceAreas" | null;

export interface PrimaryNavItem {
  name: string;
  href: string;
  dropdown?: Exclude<DropdownKey, null>;
}

export const primaryNav: PrimaryNavItem[] = [
  { name: "Services", href: "/services", dropdown: "services" },
  { name: "Service Areas", href: "/service-areas", dropdown: "serviceAreas" },
  { name: "Resources", href: "/resources", dropdown: "resources" },
  { name: "About", href: "/about", dropdown: "about" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
  { name: "Ayuda", href: "/ayuda" },
];

